<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  getWidgetLocaleMessages,
  readWidgetLocaleText,
} from 'dashboard/helper/widgetLocale';

const DEFAULT_GREETING_CARD = {
  enabled: true,
  mediaUrl: '/handwave.gif',
  title: 'Hi, let us know if you have any questions.',
  description: '',
  primaryButtonText: 'Chat now',
  primaryButtonPayload: 'Chat now',
  secondaryButtonText: 'Just browsing',
  secondaryButtonPayload: 'Just browsing',
};

const buildDefaultGreetingCard = widgetLocaleMessages => {
  const title = readWidgetLocaleText(
    widgetLocaleMessages,
    'START_NEW_CONVERSATION',
    DEFAULT_GREETING_CARD.title
  );
  const primaryButtonText = readWidgetLocaleText(
    widgetLocaleMessages,
    'START_CONVERSATION',
    DEFAULT_GREETING_CARD.primaryButtonText
  );
  const secondaryButtonText = readWidgetLocaleText(
    widgetLocaleMessages,
    'CONTINUE_CONVERSATION',
    DEFAULT_GREETING_CARD.secondaryButtonText
  );

  return {
    enabled: true,
    mediaUrl: DEFAULT_GREETING_CARD.mediaUrl,
    title,
    description: '',
    primaryButtonText,
    primaryButtonPayload: primaryButtonText,
    secondaryButtonText,
    secondaryButtonPayload: secondaryButtonText,
  };
};

export default {
  name: 'GreetingCardSettings',
  components: { NextButton },
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      // Accordion state — "content" open by default
      expandedSection: 'content',
      // Form fields
      enabled: true,
      mediaUrl: DEFAULT_GREETING_CARD.mediaUrl,
      mediaFile: null,
      title: DEFAULT_GREETING_CARD.title,
      description: '',
      primaryButtonText: DEFAULT_GREETING_CARD.primaryButtonText,
      secondaryButtonText: DEFAULT_GREETING_CARD.secondaryButtonText,
      isSaving: false,
      // Preview
      previewDocHtml: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    widgetLocaleMessages() {
      return getWidgetLocaleMessages(this.inbox.widget_language);
    },
    localizedDefaultCard() {
      return buildDefaultGreetingCard(this.widgetLocaleMessages);
    },
    greetingTitlePlaceholder() {
      return this.localizedDefaultCard.title;
    },
    greetingDescriptionPlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'TEAM_AVAILABILITY.BACK_AS_SOON_AS_POSSIBLE',
        'Optional subtitle shown below the title.'
      );
    },
    primaryButtonPlaceholder() {
      return this.localizedDefaultCard.primaryButtonText;
    },
    secondaryButtonPlaceholder() {
      return this.localizedDefaultCard.secondaryButtonText;
    },
    saveSettingsLabel() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'COMPONENTS.FORM_BUBBLE.SUBMIT',
        'Save settings'
      );
    },
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    greetingCardConfig() {
      const defaults = this.localizedDefaultCard;
      return {
        enabled: !!this.enabled,
        mediaUrl: this.mediaUrl || defaults.mediaUrl,
        title: this.title || defaults.title,
        description: this.description || '',
        primaryButtonText: this.primaryButtonText || defaults.primaryButtonText,
        primaryButtonPayload:
          this.primaryButtonText || defaults.primaryButtonPayload,
        secondaryButtonText:
          this.secondaryButtonText || defaults.secondaryButtonText,
        secondaryButtonPayload:
          this.secondaryButtonText || defaults.secondaryButtonPayload,
      };
    },
    liveChatwootSettings() {
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      return {
        locale: this.inbox.widget_language || 'en',
        position: this.inbox.position || 'right',
        type: 'standard',
        allowThemeOverride: true,
        darkMode: isMidnight ? 'dark' : 'light',
        themePreset: this.inbox.widget_theme_preset || 'classic',
        widgetStyle: 'rounded',
        widgetColor: this.inbox.widget_color || '#0059e1',
        iconColor: '#ffffff',
        themeConfig: {
          ...(this.inbox.widget_theme_config || {}),
          richGreetingCard: this.greetingCardConfig,
        },
        welcomeTitle: this.inbox.welcome_title || '',
        welcomeDescription: this.inbox.welcome_tagline || '',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        // No initialRoute → widget opens to HOME screen, greeting card is visible
      };
    },
  },
  watch: {
    inbox() {
      this.loadFromInbox();
      this.previewDocHtml = this.buildPreviewDoc();
    },
    enabled() {
      this.pushSettingsToPreview();
    },
    mediaUrl() {
      this.pushSettingsToPreview();
    },
    title() {
      this.pushSettingsToPreview();
    },
    description() {
      this.pushSettingsToPreview();
    },
    primaryButtonText() {
      this.pushSettingsToPreview();
    },
    secondaryButtonText() {
      this.pushSettingsToPreview();
    },
  },
  mounted() {
    this.loadFromInbox();
    this.previewDocHtml = this.buildPreviewDoc();
  },
  methods: {
    loadFromInbox() {
      const cfg = this.inbox.widget_theme_config?.richGreetingCard || {};
      const defaults = this.localizedDefaultCard;
      this.enabled = cfg.enabled ?? true;
      this.mediaUrl = cfg.mediaUrl || defaults.mediaUrl;
      this.title = cfg.title || defaults.title;
      this.description = cfg.description || '';
      this.primaryButtonText = cfg.primaryButtonText || defaults.primaryButtonText;
      this.secondaryButtonText =
        cfg.secondaryButtonText || defaults.secondaryButtonText;
    },
    toggleSection(key) {
      this.expandedSection = this.expandedSection === key ? null : key;
    },
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const bg =
        isMidnight && this.inbox.widget_theme_config?.background
          ? this.inbox.widget_theme_config.background
          : isMidnight
            ? '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);
      const SO = '\x3cscript\x3e';
      const SC = '\x3c\x2fscript\x3e';
      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style>` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        // Hide the launcher bubble — we only want to see the open widget
        `.woot--bubble-holder{visibility:hidden !important;}` +
        `</style>` +
        `</head><body>` +
        SO +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){` +
        `window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        // Open widget to show home screen (greeting card visible)
        `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
        `};` +
        `})(document,"script");` +
        SC +
        `</body></html>`
      );
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe?.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (e) {
        // ignore
      }
    },
    onMediaUpload(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;
      this.mediaFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.mediaUrl = String(e?.target?.result || '').trim();
      };
      reader.readAsDataURL(file);
    },
    clearMedia() {
      this.mediaFile = null;
      this.mediaUrl = '';
    },
    openWebsitePreview() {
      const url = this.inbox.website_url || '';
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async save() {
      this.isSaving = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          channel: {
            widget_theme_config: {
              ...(this.inbox.widget_theme_config || {}),
              richGreetingCard: this.greetingCardConfig,
            },
          },
        });
        useAlert('Greeting card saved');
      } catch (_) {
        useAlert('Unable to save greeting card');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="gc-page">
    <div class="gc-layout">
      <!-- ═══════════════════════════════════════════════
           LEFT PANEL: Settings
           ═══════════════════════════════════════════════ -->
      <div class="gc-settings-panel">
        <!-- Header bar (56px, white, border-bottom) -->
        <div class="gc-header-bar">
          <div class="gc-header-title">Greeting card</div>
        </div>

        <!-- Scrollable content area -->
        <div class="gc-content-scroll">
          <div class="gc-content-inner">
            <form @submit.prevent>
              <!-- ─── Enable / Disable ─── -->
              <div class="gc-field-label">Greeting card</div>
              <div class="gc-enable-row">
                <label class="gc-toggle-label">
                  <div
                    class="gc-toggle"
                    :class="{ on: enabled }"
                    @click="enabled = !enabled"
                  >
                    <span class="gc-toggle-knob" />
                  </div>
                  <span class="gc-toggle-text">{{
                    enabled ? 'Enabled' : 'Disabled'
                  }}</span>
                </label>
                <p class="gc-enable-hint">
                  When enabled, the greeting card is shown on the home screen
                  before customers start chatting.
                </p>
              </div>

              <!-- ─── Customize phrases label ─── -->
              <div class="gc-field-label" style="margin-top: 24px">
                Customize greeting card
              </div>

              <!-- ── Accordion: Greeting card content ── -->
              <div class="gc-accordions">
                <div class="gc-accordion" tabindex="0">
                  <div
                    class="gc-accordion__header"
                    :aria-expanded="expandedSection === 'content'"
                    @click="toggleSection('content')"
                  >
                    <span class="gc-accordion__trigger-inner">
                      <span class="gc-accordion__icon">
                        <!-- Hand/wave icon -->
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            fill="currentcolor"
                            d="M14.53 2.224a2.5 2.5 0 0 0-2.124-.398 2.5 2.5 0 0 0-1.518 1.164L8.332 7.417a2.87 2.87 0 0 0-2.717-1.822 2.5 2.5 0 0 0-2.459 2.472c-.042 3.807-.05 6.053-.017 6.667l.02.363v.001a7 7 0 0 0 3.203 5.478q.05.037.106.07l1.732 1a7 7 0 0 0 9.562-2.562l4.25-7.361a2.5 2.5 0 0 0-.706-3.28l-2.873 4.977a.5.5 0 0 1-.866-.5l3.176-5.5A2.5 2.5 0 0 0 19.1 4.89l-3.565 6.175a.5.5 0 1 1-.866-.5l4.25-7.362a.5.5 0 0 1 .066-.09 2.5 2.5 0 0 0-2.937-1.53 1 1 0 0 1-.035.073l-4.25 7.361a.5.5 0 1 1-.866-.5z"
                          />
                        </svg>
                      </span>
                      <span class="gc-accordion__text">
                        <span class="gc-accordion__title">Greeting content</span>
                      </span>
                      <span class="gc-accordion__chevron">
                        <svg
                          v-if="expandedSection === 'content'"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 9 6 6 6-6"
                          />
                        </svg>
                        <svg
                          v-else
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m9 6 6 6-6 6"
                          />
                        </svg>
                      </span>
                    </span>
                  </div>
                  <div
                    class="gc-accordion__body"
                    :class="{ collapsed: expandedSection !== 'content' }"
                  >
                    <div class="gc-accordion__body-inner">
                      <div class="gc-fields-grid">
                        <div class="gc-field gc-field--full">
                          <label class="gc-label" for="gc-title">Greeting message / title</label>
                          <div class="gc-input-wrap">
                            <input
                              id="gc-title"
                              v-model="title"
                              type="text"
                              class="gc-input"
                              :placeholder="greetingTitlePlaceholder"
                            />
                          </div>
                        </div>
                        <div class="gc-field gc-field--full">
                          <label class="gc-label" for="gc-description">Description
                            <span style="font-weight: 400; color: #9ca3af">(optional)</span></label>
                          <div class="gc-input-wrap">
                            <div class="gc-slate-wrap">
                              <textarea
                                id="gc-description"
                                v-model="description"
                                class="gc-textarea"
                                rows="3"
                                :placeholder="greetingDescriptionPlaceholder"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="gc-field gc-field--full">
                          <label class="gc-label" for="gc-media">GIF / image URL
                            <span style="font-weight: 400; color: #9ca3af">(optional)</span></label>
                          <div class="gc-input-wrap">
                            <input
                              id="gc-media"
                              v-model="mediaUrl"
                              type="text"
                              class="gc-input"
                              placeholder="/handwave.gif"
                            />
                          </div>
                          <div class="gc-media-upload-row">
                            <label class="gc-upload-btn">
                              Upload image
                              <input
                                type="file"
                                accept="image/*,.gif,.webp"
                                class="gc-upload-hidden"
                                @change="onMediaUpload"
                              />
                            </label>
                            <button
                              type="button"
                              class="gc-clear-btn"
                              @click="clearMedia"
                            >
                              Clear
                            </button>
                          </div>
                          <div v-if="mediaUrl" class="gc-media-preview">
                            <img
                              :src="mediaUrl"
                              alt="Greeting card media"
                              class="gc-media-img"
                              @error="mediaUrl = ''"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ── Accordion: Call to action buttons ── -->
                <div class="gc-accordion" tabindex="0">
                  <div
                    class="gc-accordion__header"
                    :aria-expanded="expandedSection === 'buttons'"
                    @click="toggleSection('buttons')"
                  >
                    <span class="gc-accordion__trigger-inner">
                      <span class="gc-accordion__icon">
                        <!-- Cursor/click icon -->
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            fill="currentcolor"
                            fill-rule="evenodd"
                            d="M8 2a1 1 0 0 1 1 1v10.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L6 13.586V3a1 1 0 0 1 1-1zm6 1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V3zm1 1v2h2V4h-2zm-1 7a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4zm1 1v2h2v-2h-2z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </span>
                      <span class="gc-accordion__text">
                        <span class="gc-accordion__title">Call-to-action buttons</span>
                        <span class="gc-accordion__subtitle">Buttons shown on the greeting card that customers can
                          click to start chatting.</span>
                      </span>
                      <span class="gc-accordion__chevron">
                        <svg
                          v-if="expandedSection === 'buttons'"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 9 6 6 6-6"
                          />
                        </svg>
                        <svg
                          v-else
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m9 6 6 6-6 6"
                          />
                        </svg>
                      </span>
                    </span>
                  </div>
                  <div
                    class="gc-accordion__body"
                    :class="{ collapsed: expandedSection !== 'buttons' }"
                  >
                    <div class="gc-accordion__body-inner">
                      <div class="gc-fields-grid">
                        <div class="gc-field">
                          <label class="gc-label" for="gc-primary-btn">Primary button label</label>
                          <div class="gc-input-wrap">
                            <input
                              id="gc-primary-btn"
                              v-model="primaryButtonText"
                              type="text"
                              class="gc-input"
                              :placeholder="primaryButtonPlaceholder"
                            />
                          </div>
                        </div>
                        <div class="gc-field">
                          <label class="gc-label" for="gc-secondary-btn">Secondary button label</label>
                          <div class="gc-input-wrap">
                            <input
                              id="gc-secondary-btn"
                              v-model="secondaryButtonText"
                              type="text"
                              class="gc-input"
                              :placeholder="secondaryButtonPlaceholder"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /gc-accordions -->

              <button type="submit" class="gc-hidden-submit" />
            </form>
          </div>
          <!-- /gc-content-inner -->
        </div>
        <!-- /gc-content-scroll -->

        <!-- Sticky save bar -->
        <div class="gc-save-bar">
          <NextButton
            class="gc-save-btn"
            type="button"
            :label="saveSettingsLabel"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="save"
          />
        </div>
      </div>
      <!-- /gc-settings-panel -->

      <!-- ═══════════════════════════════════════════════
           RIGHT PANEL: Preview (home screen → greeting card visible)
           ═══════════════════════════════════════════════ -->
      <div class="gc-preview-panel">
        <!-- Preview header bar (gray, 56px) -->
        <div class="gc-preview-header-bar">
          <div class="gc-preview-title">Preview</div>
        </div>

        <!-- Preview content area -->
        <div class="gc-preview-content-area">
          <div class="gc-preview-scroll-inner">
            <div class="gc-preview-widget-holder">
              <div class="gc-preview-widget-stage">
                <div class="gc-preview-widget-inner">
                  <iframe
                    v-if="previewDocHtml"
                    ref="sdkFrame"
                    class="gc-preview-iframe"
                    :srcdoc="previewDocHtml"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  />
                  <div v-else class="gc-preview-empty">
                    Widget preview unavailable for this inbox.
                  </div>
                </div>
              </div>
            </div>
            <p class="gc-preview-test-note">
              <a href="#" @click.prevent="openWebsitePreview">Test it out</a> on
              the chat page
            </p>
          </div>
        </div>
      </div>
      <!-- /gc-preview-panel -->
    </div>
    <!-- /gc-layout -->
  </div>
  <!-- /gc-page -->
</template>

<style scoped>
/* ══════════════════════════════════════════════════════
   Root & layout — mirrors LanguageSettings exactly
   ══════════════════════════════════════════════════════ */
.gc-page {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.gc-layout {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ══════════════════════════════════════════════════════
   LEFT PANEL
   ══════════════════════════════════════════════════════ */
.gc-settings-panel {
  flex: 5 1 62.5%;
  min-width: 296px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #111827;
  box-sizing: border-box;
}

.gc-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
  box-sizing: border-box;
  padding-left: 21px;
}

.gc-header-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
}

.gc-content-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.gc-content-inner {
  padding: 32px 32px 120px;
  box-sizing: border-box;
  max-width: 960px;
}

/* Section labels */
.gc-field-label {
  line-height: 24px;
  color: #111827;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 0;
}

/* ══════════════════════════════════════════════════════
   Enable toggle
   ══════════════════════════════════════════════════════ */
.gc-enable-row {
  margin: 12px 0 0;
}

.gc-toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.gc-toggle {
  width: 40px;
  height: 22px;
  border-radius: 999px;
  background: #d1d5db;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.gc-toggle.on {
  background: #0059e1;
}

.gc-toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.gc-toggle.on .gc-toggle-knob {
  transform: translateX(18px);
}

.gc-toggle-text {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.gc-enable-hint {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 20px;
  color: #6b7280;
}

/* ══════════════════════════════════════════════════════
   Accordions (mirrors LanguageSettings)
   ══════════════════════════════════════════════════════ */
.gc-accordions {
  margin: 15px 0 40px;
  font-size: 13px;
  color: #111827;
}

.gc-accordion {
  border: solid 1px #eeeeef;
  margin-bottom: 12px;
  border-radius: 4px;
  background: #fff;
  width: 100%;
  max-width: 705px;
  box-sizing: border-box;
  outline: none;
}

.gc-accordion__header {
  min-height: 60px;
  font-size: 16px;
  cursor: pointer;
  color: #111827;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  border-color: transparent;
  padding: 0 17px;
  user-select: none;
}

.gc-accordion__header:hover {
  background: #f9fafb;
}

.gc-accordion__trigger-inner {
  display: inline-flex;
  align-items: center;
  padding: 17px 0;
  width: 100%;
  line-height: 24px;
}

.gc-accordion__icon {
  display: flex;
  justify-content: center;
  fill: #111827;
  align-self: flex-start;
  width: 18px;
  height: 24px;
  margin-right: 12px;
  flex-shrink: 0;
  color: #111827;
}

.gc-accordion__text {
  display: flex;
  flex-flow: column;
  width: 100%;
}

.gc-accordion__title {
  display: flex;
  width: 100%;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.gc-accordion__subtitle {
  max-width: 90%;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  padding-top: 2px;
  color: #6b7280;
}

.gc-accordion__chevron {
  fill: #111827;
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: #111827;
}

/* Accordion body (animated) */
.gc-accordion__body {
  position: relative;
  transition:
    max-height 0.2s ease,
    opacity 0.2s ease;
  opacity: 1;
  max-height: 1200px;
}

.gc-accordion__body.collapsed {
  overflow: hidden;
  visibility: collapse;
  opacity: 0;
  z-index: -1;
  max-height: 0 !important;
}

.gc-accordion__body-inner {
  padding: 0 16px 16px;
}

/* Fields grid inside accordion */
.gc-fields-grid {
  display: flex;
  flex-wrap: wrap;
  margin-top: -16px;
  margin-right: -30px;
}

.gc-field {
  min-width: 200px;
  flex: 1 0 calc(50% - 30px);
  margin-top: 16px;
  margin-right: 30px;
  box-sizing: border-box;
}

.gc-field--full {
  flex: 1 0 calc(100% - 30px);
}

.gc-label {
  display: block;
  font-size: 13px;
  line-height: 20px;
  color: #111827;
  cursor: pointer;
}

.gc-input-wrap {
  position: relative;
  width: 100%;
}

.gc-input {
  display: block;
  width: 100%;
  margin-top: 6px;
  font-size: 14px;
  padding: 11px 15px 10px;
  line-height: 22px;
  color: #111827;
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  transition: border 0.25s ease-in-out;
}

.gc-input:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.12);
}

.gc-slate-wrap {
  position: relative;
  width: 100%;
  display: block;
}

.gc-textarea {
  display: block;
  width: 100%;
  margin-top: 6px;
  font-size: 14px;
  padding: 10px;
  line-height: 18px;
  color: #111827;
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  resize: vertical;
  min-height: 70px;
  transition: border 0.25s ease-in-out;
  font-family: inherit;
}

.gc-textarea:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.12);
}

/* Media upload */
.gc-media-upload-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  align-items: center;
}

.gc-upload-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid #bcc6d0;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  background: #fff;
  color: #374151;
  position: relative;
  transition: background 0.15s;
}

.gc-upload-btn:hover {
  background: #f5f5f5;
}

.gc-upload-hidden {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.gc-clear-btn {
  padding: 6px 12px;
  border: 1px solid #bcc6d0;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  background: #fff;
  color: #6b7280;
  transition: background 0.15s;
}

.gc-clear-btn:hover {
  background: #f5f5f5;
}

.gc-media-preview {
  margin-top: 10px;
}

.gc-media-img {
  max-width: 80px;
  max-height: 80px;
  border-radius: 8px;
  border: 1px solid #eeeeef;
  object-fit: contain;
}

/* ══════════════════════════════════════════════════════
   Save bar (sticky bottom)
   ══════════════════════════════════════════════════════ */
.gc-hidden-submit {
  display: none;
}

.gc-save-bar {
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  flex-shrink: 0;
  background: #fff;
}

.gc-save-bar :deep(.gc-save-btn) {
  height: 36px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  background: #0059e1 !important;
  border: 1px solid #0059e1 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

.gc-save-bar :deep(.gc-save-btn:hover) {
  background: #0047c2 !important;
  border-color: #0047c2 !important;
}

/* ══════════════════════════════════════════════════════
   RIGHT PANEL (Preview) — mirrors LanguageSettings exactly
   ══════════════════════════════════════════════════════ */
.gc-preview-panel {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.gc-preview-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.gc-preview-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  margin-left: 20px;
}

.gc-preview-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;
}

.gc-preview-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

/* Widget holder — matches LiveChat .css-ht54w1 */
.gc-preview-widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.gc-preview-widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.gc-preview-widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

/* iframe scaled 0.8 to match LiveChat preview */
.gc-preview-iframe {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.gc-preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

.gc-preview-test-note {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 8px 0 16px;
}

.gc-preview-test-note a {
  color: #0059e1;
  text-decoration: none;
}

.gc-preview-test-note a:hover {
  text-decoration: underline;
}
</style>
